import React, { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../Layout/index.jsx";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";
import { Table } from "flowbite-react";
import { FaDownload } from "react-icons/fa6";
import { IoMdArrowRoundBack } from "react-icons/io";
import {
  useGetCampusByNameQuery,
  useGetClustersByCampusQuery,
  useGetStudentsByCampusQuery,
} from "../../../../graphql/generated.tsx";
import { gql, useQuery } from "@apollo/client";
import Loader from "../../../../components/Loader.jsx";

const GET_MODULE_LEADERBOARD = gql`
  query getModuleLeaderboard(
    $moduleId: String!
    $campusName: String!
    $dayIds: [String!]
  ) {
    getModuleLeaderboard(
      moduleId: $moduleId
      campusName: $campusName
      dayIds: $dayIds
    ) {
      student_id
      question_id
      score
      is_completed
    }
  }
`;

const ModuleNotAttended = () => {
  const { campusName, moduleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const moduleDetails = location.state?.moduleDetails;
  const [selectedDayIndexes, setSelectedDayIndexes] = useState([]);
  const [isDayFilterOpen, setIsDayFilterOpen] = useState(false);
  const [selectedClusterId, setSelectedClusterId] = useState("");
  const hasInitDayFiltersRef = useRef(false);

  const { data: campusData, loading: campusLoading } = useGetCampusByNameQuery({
    variables: { name: campusName },
    skip: !campusName,
  });

  const campusId = campusData?.getCampusByName?.id;

  const { data: clustersData, loading: clustersLoading } = useGetClustersByCampusQuery({
    variables: { campusName, limit: 200 },
    skip: !campusName,
  });

  const selectedDayIds = useMemo(() => {
    const days = moduleDetails?.daywise_plan || [];
    if (days.length === 0) return undefined;

    const indexes = selectedDayIndexes.length > 0
      ? selectedDayIndexes
      : days.map((_, idx) => idx);

    const ids = indexes
      .map((idx) => days[idx]?.day_id)
      .filter(Boolean);

    return ids.length > 0 ? ids : undefined;
  }, [moduleDetails, selectedDayIndexes]);

  const dayFilterLabel = useMemo(() => {
    const total = moduleDetails?.daywise_plan?.length || 0;
    if (total === 0) return "Select all";
    if (selectedDayIndexes.length === 0 || selectedDayIndexes.length === total) {
      return "Select all";
    }
    if (selectedDayIndexes.length === 1) {
      const idx = selectedDayIndexes[0];
      const date = moduleDetails?.enabled_dates?.[idx];
      return date
        ? new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : `Day ${idx + 1}`;
    }
    return `${selectedDayIndexes.length} selected`;
  }, [moduleDetails, selectedDayIndexes]);

  useEffect(() => {
    if (hasInitDayFiltersRef.current) return;
    const days = moduleDetails?.daywise_plan || [];
    if (days.length === 0) return;

    setSelectedDayIndexes(days.map((_, idx) => idx));
    hasInitDayFiltersRef.current = true;
  }, [moduleDetails]);

  const { data: studentsData, loading: studentsLoading } = useGetStudentsByCampusQuery({
    variables: {
      campusId,
      clusterid: selectedClusterId || undefined,
      page: 1,
      limit: 10000,
      searchTerm: "",
    },
    skip: !campusId,
    fetchPolicy: "cache-and-network",
  });

  const { data: leaderboardData, loading: leaderboardLoading } = useQuery(GET_MODULE_LEADERBOARD, {
    variables: { moduleId, campusName, dayIds: selectedDayIds },
    skip: !moduleId || !campusName,
  });

  const isLoading =
    campusLoading ||
    clustersLoading ||
    studentsLoading ||
    leaderboardLoading;

  const allStudents = studentsData?.getStudentsByCampus?.students || [];
  const leaderboard = leaderboardData?.getModuleLeaderboard || [];

  const filteredQuestionIds = useMemo(() => {
    const days = moduleDetails?.daywise_plan || [];
    const indexes = selectedDayIndexes.length > 0
      ? selectedDayIndexes
      : days.map((_, idx) => idx);
    const ids = new Set();
    indexes.forEach((idx) => {
      const day = days[idx];
      if (!day?.questions) return;
      day.questions.forEach((qid) => ids.add(qid));
    });
    return ids;
  }, [moduleDetails, selectedDayIndexes]);

  const notAttendedStudents = useMemo(() => {
    const attendedIds = new Set();
    leaderboard.forEach((entry) => {
      if (filteredQuestionIds.size === 0) return;
      if (
        filteredQuestionIds.has(entry.question_id) &&
        (entry.is_completed || entry.score > 0)
      ) {
        attendedIds.add(entry.student_id);
      }
    });
    return allStudents.filter((student) => !attendedIds.has(student.id));
  }, [allStudents, leaderboard, filteredQuestionIds]);

  const handleDownloadCSV = () => {
    if (notAttendedStudents.length === 0) return;

    const rows = notAttendedStudents.map((student) => ({
      Name: student.name,
      Roll_Number: student.rollNo,
      Department: student.department || "-",
      Email: student.email,
    }));

    const header = Object.keys(rows[0]).join(",") + "\n";
    const csvContent =
      header +
      rows
        .map((r) =>
          Object.values(r)
            .map((val) => `"${(val || "").toString().replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "module_not_attended.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="p-8 pt-5 m-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)}>
              <IoMdArrowRoundBack className="w-5 h-5 text-gray-600" />
            </button>
            <h2 className="text-2xl font-bold">Not Attended</h2>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={selectedClusterId}
              onChange={(e) => setSelectedClusterId(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
            >
              <option value="">All Clusters</option>
              {(clustersData?.getClustersByCampus || []).map((cluster) => (
                <option key={cluster.id} value={cluster.id}>
                  {cluster.name}
                </option>
              ))}
            </select>

            <div className="relative flex flex-col gap-1">
              <span className="text-xs font-medium text-gray-600">
                Date filter
              </span>
              <button
                type="button"
                onClick={() => setIsDayFilterOpen((prev) => !prev)}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white flex items-center justify-between gap-3 min-w-[160px]"
              >
                <span className="text-gray-800">{dayFilterLabel}</span>
                <span className="text-gray-400">v</span>
              </button>
              {isDayFilterOpen && (
                <div className="absolute right-0 mt-2 w-[360px] bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-20">
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={
                          moduleDetails?.daywise_plan?.length > 0 &&
                          selectedDayIndexes.length === moduleDetails.daywise_plan.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDayIndexes(
                              moduleDetails?.daywise_plan?.map((_, idx) => idx) || []
                            );
                          } else {
                            setSelectedDayIndexes([]);
                          }
                        }}
                        className="accent-blue-600"
                      />
                      All
                    </label>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 max-h-40 overflow-y-auto">
                    {moduleDetails?.daywise_plan?.map((day, idx) => {
                      const date = moduleDetails?.enabled_dates?.[idx];
                      const formattedDate = date
                        ? new Date(date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : `Day ${idx + 1}`;
                      return (
                        <label key={day.day_id || idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={selectedDayIndexes.includes(idx)}
                            onChange={() => {
                              setSelectedDayIndexes((prev) =>
                                prev.includes(idx)
                                  ? prev.filter((i) => i !== idx)
                                  : [...prev, idx]
                              );
                            }}
                            className="accent-blue-600"
                          />
                          {formattedDate}
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => setIsDayFilterOpen(false)}
                      className="px-3 py-1.5 text-sm text-white bg-gray-500 border border-dashed border-gray-400 rounded"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleDownloadCSV}
              className="inline-flex items-center justify-center w-10 h-10 text-gray-600 transition-all bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:text-gray-900"
              title="Download"
              aria-label="Download"
            >
              <FaDownload size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table className="min-w-[600px] text-gray-500 shadow-none border-none">
            <Table.Head style={{ backgroundColor: "#5c7a94" }}>
              <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className="px-3 py-2 text-[10px] text-center text-white sm:px-4 sm:py-2.5 sm:text-xs">S.No</Table.HeadCell>
              <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className="px-3 py-2 text-[10px] text-center text-white sm:px-4 sm:py-2.5 sm:text-xs">Name</Table.HeadCell>
              <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className="px-3 py-2 text-[10px] text-center text-white sm:px-4 sm:py-2.5 sm:text-xs">Roll No</Table.HeadCell>
              <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className="px-3 py-2 text-[10px] text-center text-white sm:px-4 sm:py-2.5 sm:text-xs">Department</Table.HeadCell>
              <Table.HeadCell style={{ backgroundColor: "#5c7a94" }} className="px-3 py-2 text-[10px] text-center text-white sm:px-4 sm:py-2.5 sm:text-xs">Email</Table.HeadCell>
            </Table.Head>
            <Table.Body className="bg-white">
              {notAttendedStudents.length > 0 ? (
                notAttendedStudents.map((student, idx) => (
                  <Table.Row key={student.id} className={`transition duration-150 ease-in-out hover:bg-blue-50/60 ${idx % 2 === 0 ? "bg-gray-50" : "bg-white"}`}>
                    <Table.Cell className="px-2 py-2 text-xs font-medium text-center text-gray-700 border-none sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base">{idx + 1}</Table.Cell>
                    <Table.Cell className="px-2 py-2 text-xs text-center border-none sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base whitespace-nowrap">{student.name}</Table.Cell>
                    <Table.Cell className="px-2 py-2 text-xs text-center border-none sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base whitespace-nowrap">{student.rollNo}</Table.Cell>
                    <Table.Cell className="px-2 py-2 text-xs text-center border-none sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base whitespace-nowrap">
                      {student.department && String(student.department).trim() !== "" ? student.department : "-"}
                    </Table.Cell>
                    <Table.Cell className="px-2 py-2 text-xs text-center border-none sm:px-4 md:px-6 sm:py-3 sm:text-sm md:text-base">{student.email}</Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell colSpan={5} className="py-4 text-xs text-center border-none sm:text-sm md:text-base">
                    All students attended.
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ModuleNotAttended;
